/**
 * routeBuilder.js - Shared route resolution utility
 * 
 * Single source of truth for route generation used by both:
 * - GuidePage.js (private)
 * - ExperienceHubCore.js (public via PublicGuidePage)
 * 
 * RULES:
 * 1. If custom route exists for destination, use it
 * 2. If no custom route exists, use fallback logic (from GuidePage.js)
 * 3. Fallback finds entrance → reception → corridor → destination
 * 4. NO auto-generated transition cards
 * 5. Only user-created movement steps are included
 */

/**
 * Build route for a destination area
 * 
 * @param {Object} params
 * @param {Array} params.allAreasWithData - All areas with sensory data
 * @param {Array} params.movementSteps - User-created movement steps
 * @param {Array} params.customRoutes - Custom route definitions (from submission.custom_routes)
 * @param {Object} params.targetArea - Destination area
 * @returns {Array} Route steps array
 */
export function buildRouteForDestination({
  allAreasWithData = [],
  movementSteps = [],
  customRoutes = [],
  targetArea
}) {
  // Defensive normalization: ensure all inputs are arrays (handles null, undefined, object, string)
  const safeAreas = Array.isArray(allAreasWithData) ? allAreasWithData : [];
  const safeCustomRoutes = Array.isArray(customRoutes) ? customRoutes : [];
  const safeMovementSteps = Array.isArray(movementSteps) ? movementSteps : [];
  
  if (!targetArea || !safeAreas.length) return [];
  
  // 1. Check for custom route for this destination
  const customRoute = safeCustomRoutes.find(
    r => r && r.destination_area_id === targetArea.area_id
  );
  
  if (customRoute && customRoute.route_steps?.length > 0) {
    // 2. Build route from saved custom steps
    return buildFromCustomRoute(customRoute, safeAreas, safeMovementSteps);
  }
  
  // 3. Use existing fallback logic (exact copy from GuidePage.js lines 968-1064)
  return buildFallbackRoute(safeAreas, safeMovementSteps, targetArea);
}

/**
 * Build route from custom route definition
 */
function buildFromCustomRoute(customRoute, allAreasWithData, movementSteps) {
  const routeWithTransitions = [];
  const steps = customRoute.route_steps || [];
  
  // Defensive: ensure movementSteps is array before calling .find()
  const safeMovementSteps = Array.isArray(movementSteps) ? movementSteps : [];
  
  // Helper to get movement step for a pair of areas
  const getMovementStepForPair = (fromAreaId, toAreaId) => {
    return safeMovementSteps.find(s => s && s.from_area_id === fromAreaId && s.to_area_id === toAreaId);
  };
  
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    
    if (step.type === 'area') {
      // Find full area data
      const areaData = allAreasWithData.find(a => a.area_id === step.area_id);
      if (areaData) {
        const isFirst = i === 0;
        const isLast = i === steps.length - 1;
        
        routeWithTransitions.push({
          ...areaData,
          stepType: isFirst ? 'start' : isLast ? 'destination' : 'waypoint',
          isTransition: false
        });
        
        // Check for movement step to next area step
        if (!isLast) {
          const nextAreaStep = steps.slice(i + 1).find(s => s.type === 'area');
          if (nextAreaStep) {
            const customStep = getMovementStepForPair(step.area_id, nextAreaStep.area_id);
            if (customStep) {
              routeWithTransitions.push({
                stepType: 'transition',
                isTransition: true,
                isCustom: true,
                customStepId: customStep.step_id,
                transitionType: 'custom',
                transitionLabel: customStep.title,
                transitionDuration: customStep.duration || '10-15 seconds',
                transitionDescription: customStep.description,
                transitionAwareness: customStep.awareness,
                fromArea: areaData.area_name || areaData.name,
                toArea: nextAreaStep.area_name || nextAreaStep.name,
                fromAreaId: step.area_id,
                toAreaId: nextAreaStep.area_id,
                area_id: `custom_${customStep.step_id}`,
                area_name: customStep.title
              });
            }
          }
        }
      }
    } else if (step.type === 'custom') {
      // Custom note step (inline direction)
      routeWithTransitions.push({
        stepType: 'transition',
        isTransition: true,
        isCustom: true,
        isInlineNote: true,
        transitionLabel: step.label,
        transitionDescription: step.label,
        transitionDuration: step.duration || '10-15 seconds',
        area_id: `note_${i}`,
        area_name: step.label
      });
    }
  }
  
  return routeWithTransitions;
}

/**
 * Build fallback route using entrance → reception → corridor → destination pattern
 * EXACT copy from GuidePage.js lines 968-1064
 */
function buildFallbackRoute(allAreasWithData, movementSteps, targetArea) {
  // Defensive: ensure arrays before any operations
  const safeAreas = Array.isArray(allAreasWithData) ? allAreasWithData : [];
  const safeMovementSteps = Array.isArray(movementSteps) ? movementSteps : [];
  
  // Helper to get movement step for a pair of areas
  const getMovementStepForPair = (fromAreaId, toAreaId) => {
    return safeMovementSteps.find(s => s && s.from_area_id === fromAreaId && s.to_area_id === toAreaId);
  };
  
  // Find key transition areas by name patterns
  const findAreaByPattern = (patterns) => {
    const lower = patterns.map(p => p.toLowerCase());
    return safeAreas.find(a => {
      const name = (a.area_name || a.name || '').toLowerCase();
      return lower.some(p => name.includes(p));
    });
  };
  
  const entrance = findAreaByPattern(['entrance', 'entry', 'front', 'main door', 'arrival']);
  const reception = findAreaByPattern(['reception', 'lobby', 'foyer', 'welcome', 'front desk']);
  const corridor = findAreaByPattern(['corridor', 'hallway', 'main hall', 'passage', 'walkway']);
  
  // Build route with transitions
  const routeAreas = [];
  const addedIds = new Set();
  
  const addArea = (area, stepType = 'waypoint') => {
    if (area && !addedIds.has(area.area_id)) {
      addedIds.add(area.area_id);
      routeAreas.push({ ...area, stepType, isTransition: false });
    }
  };
  
  // Start point
  if (entrance) {
    addArea(entrance, 'start');
  } else if (safeAreas[0] && safeAreas[0].area_id !== targetArea.area_id) {
    addArea(safeAreas[0], 'start');
  }
  
  // Add reception if different from entrance and target
  if (reception && reception.area_id !== targetArea.area_id) {
    addArea(reception, 'waypoint');
  }
  
  // Add corridor if different from all above and target
  if (corridor && corridor.area_id !== targetArea.area_id) {
    addArea(corridor, 'waypoint');
  }
  
  // Target area
  addArea(targetArea, 'destination');
  
  // If route is empty or just target, create minimal route
  if (routeAreas.length === 0) {
    return [{ ...targetArea, stepType: 'destination', isTransition: false }];
  }
  
  // If target wasn't added (was same as start), ensure it's there
  if (!addedIds.has(targetArea.area_id)) {
    routeAreas.push({ ...targetArea, stepType: 'destination', isTransition: false });
  }
  
  // Now interleave ONLY user-created custom movement steps between areas
  // DO NOT auto-generate transition cards - movement steps must be manual only
  const routeWithTransitions = [];
  for (let i = 0; i < routeAreas.length; i++) {
    routeWithTransitions.push(routeAreas[i]);
    
    // Add custom movement step between areas ONLY if user has created one
    if (i < routeAreas.length - 1) {
      const fromArea = routeAreas[i];
      const toArea = routeAreas[i + 1];
      
      // Check for custom movement step for this pair
      const customStep = getMovementStepForPair(fromArea.area_id, toArea.area_id);
      
      if (customStep) {
        // Only include user-created movement steps
        routeWithTransitions.push({
          stepType: 'transition',
          isTransition: true,
          isCustom: true,
          customStepId: customStep.step_id,
          transitionType: 'custom',
          transitionLabel: customStep.title,
          transitionDuration: customStep.duration || '10-15 seconds',
          transitionDescription: customStep.description,
          transitionAwareness: customStep.awareness,
          fromArea: fromArea.area_name || fromArea.name,
          toArea: toArea.area_name || toArea.name,
          fromAreaId: fromArea.area_id,
          toAreaId: toArea.area_id,
          area_id: `custom_${customStep.step_id}`,
          area_name: customStep.title
        });
      }
      // NO ELSE - do not add auto-generated transitions
    }
  }
  
  return routeWithTransitions;
}

export default buildRouteForDestination;
